import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import classnames from '../../util/classnames';
import { Logo } from '../_general/Logo';
import CentralizedFullScreenContainer from '../CentralizedFullScreenContainer';

type LoaderProps = {
  className?: string;
  children?: React.ReactNode;
  sm?: boolean;
  text?: string | React.ReactNode;
  color?: string;
  size?: number | null;
  padding?: number;
  fullscreen?: boolean;
  dots?: boolean;
  spinner?: boolean;
  inline?: boolean;
};

function Loader({
  className,
  children,
  sm,
  text,
  color = 'primary',
  size = null,
  padding = 4,
  fullscreen,
  dots = false,
  spinner = false,
  inline = false,
}: LoaderProps) {
  const loaderSize = useMemo(() => size ?? 35, [size]);

  const logoStyle = {
    width: loaderSize,
    height: loaderSize,
  };

  const loaderImage = useMemo(() => {
    if (spinner) {
      return <div className={`spinner-border${sm ? ' spinner-border-sm' : ''} text-${color}`} role="status" />;
    }

    if (dots) {
      return <div className={classnames('dots-loader', className, { sm })} />;
    }

    const AnyLogo = Logo as any;
    return (
      <div className="logo-loader" style={logoStyle}>
        <AnyLogo style={{ width: '100%', height: '100%' }} />
      </div>
    );
  }, [dots, spinner]);

  const loader = (
    <div className={classnames(`modern-loader`, { sm, 'flex items-center flex-row': inline, 'flex-col': !inline })}>
      <LogoWrapperDiv fullscreen={fullscreen} loaderSize={loaderSize}>
        {loaderImage}
      </LogoWrapperDiv>
      {children && <div className="loader-text italic">{children}</div>}
    </div>
  );

  return fullscreen ? (
    <CentralizedFullScreenContainer>
      {loader}
      {text && <p className={`text-${color} mt-3 italic`}>{text}</p>}
    </CentralizedFullScreenContainer>
  ) : (
    <div className="loader-center">{loader}</div>
  );
}

const LogoWrapperDiv = styled('div')<{
  fullscreen?: boolean;
  loaderSize: number;
}>(({ loaderSize }) => ({
  position: 'relative',
  width: loaderSize ?? 40,
  height: loaderSize ?? 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export default Loader;
